import { parsearFecha, parsearMonto, montosEnLinea, limpiarConcepto, round2, fechaDesdePartes } from "./helpers";

/**
 * Plantilla de BANREGIO (Banco Regional).
 *
 * Formato de la tabla de movimientos:
 *   DIA | CONCEPTO | CARGOS | ABONOS | SALDO
 *
 * Retos propios de Banregio:
 *  - La fecha es SÓLO el día ("05"); el mes/año vienen del periodo del encabezado
 *    ("del 01 al 31 de AGOSTO 2021").
 *  - En movimientos de varias líneas, el día queda SUELTO en su propio renglón
 *    DESPUÉS de la descripción (p.ej. el "13" aparece tras 3 líneas de concepto).
 *  - La descripción se parte en varias líneas.
 *  - Columnas Cargos/Abonos: al extraer texto se colapsan, así que el tipo se
 *    deduce por la variación del saldo (sube = depósito/cargo, baja = retiro/abono),
 *    no por la posición de la columna.
 */

const MESES_NOMBRE = {
  enero: 0, febrero: 1, marzo: 2, abril: 3, mayo: 4, junio: 5,
  julio: 6, agosto: 7, septiembre: 8, setiembre: 8, octubre: 9,
  noviembre: 10, diciembre: 11,
};

// Encabezado de la tabla: "DIA CONCEPTO CARGOS ABONOS SALDO".
const RE_HEADER = /\bdia\b/i;
const RE_TOTAL = /^\s*total\b/i;
// Día al inicio del renglón seguido del concepto (día + espacio + letra), p.ej. "05 DOC".
const RE_DIA_INICIO = /^\s*(\d{1,2})\s+(?=[A-Za-zÁÉÍÓÚáéíóú])/;
// Renglón que es sólo un número de día (el día suelto de los movimientos multilínea).
const RE_SOLO_DIA = /^\s*(\d{1,2})\s*$/;

function esHeader(linea) {
  return RE_HEADER.test(linea) && /concepto/i.test(linea) && /saldo/i.test(linea);
}

function esMovimiento(linea) {
  if (esHeader(linea) || RE_TOTAL.test(linea)) return false;
  return montosEnLinea(linea).length >= 2; // importe + saldo
}

// Mes/año del periodo: "del 01 al 31 de AGOSTO 2021".
function detectarPeriodo(lineas) {
  for (const l of lineas) {
    const m = l
      .toLowerCase()
      .match(/(enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|setiembre|octubre|noviembre|diciembre)\s+(?:de\s+)?(\d{4})/);
    if (m) return { mes: MESES_NOMBRE[m[1]], anio: parseInt(m[2], 10) };
  }
  return null;
}

function detectarSaldoInicial(lineas) {
  for (let i = 0; i < lineas.length; i++) {
    if (/saldo\s+inicial/i.test(lineas[i])) {
      const ms = montosEnLinea(lineas[i]);
      if (ms.length) return ms[0];
      for (let j = i + 1; j <= i + 2 && j < lineas.length; j++) {
        const sig = montosEnLinea(lineas[j]);
        if (sig.length) return sig[0];
      }
    }
  }
  return null;
}

const banregio = {
  id: "banregio",
  nombre: "Banregio",
  clavesSat: ["058"],
  // Detección específica: evita falsos positivos cuando "BANREGIO" aparece sólo
  // como banco emisor de un SPEI en el estado de cuenta de OTRO banco.
  detectar: (texto) => /banco regional|brm940216eq6|banregio grupo financiero/i.test(texto),
  // Encabezado: período "del 01 al 31 de AGOSTO 2021" + saldo final.
  extraerEncabezado(lineas) {
    const t = (lineas || []).join("\n");
    const per = t.match(/del\s+(\d{1,2})\s+al\s+(\d{1,2})\s+de\s+([a-záéíóú]+)\s+(\d{4})/i);
    const sf = t.match(/saldo\s+final\b[^$\d]*\$?\s*(-?\d{1,3}(?:,\d{3})*\.\d{2})/i);
    return {
      fechaInicial: per ? fechaDesdePartes(per[1], per[3], per[4]) : null,
      fechaFinal: per ? fechaDesdePartes(per[2], per[3], per[4]) : null,
      saldoFinal: sf ? parsearMonto(sf[1]) : null,
    };
  },
  parsear(lineas, anioRef) {
    const arr = lineas || [];

    // Acotar a la sección de movimientos: del encabezado a la fila "Total".
    let ini = arr.findIndex(esHeader);
    if (ini === -1) ini = -1;
    let fin = arr.length;
    for (let i = ini + 1; i < arr.length; i++) {
      if (RE_TOTAL.test(arr[i])) { fin = i; break; }
    }
    const seccion = arr.slice(ini + 1, fin);

    const periodo = detectarPeriodo(arr);
    let prevSaldo = detectarSaldoInicial(arr);

    const movimientos = [];
    let i = 0;
    while (i < seccion.length) {
      const linea = seccion[i];
      if (!esMovimiento(linea)) { i++; continue; }

      const ms = montosEnLinea(linea);
      const saldo = ms[ms.length - 1];
      const monto = Math.abs(ms[ms.length - 2]);

      // Día: al inicio de la línea, o suelto en el bloque de continuación.
      const mDia = linea.match(RE_DIA_INICIO);
      let dia = mDia ? parseInt(mDia[1], 10) : null;

      // Concepto: texto entre el día (si lo hay) y el primer importe.
      const inicioConcepto = mDia ? mDia[0].length : 0;
      const idxMonto = linea.search(/-?\(?\$?\s?\d{1,3}(?:,\d{3})*\.\d{2}/);
      const partesConcepto = [
        linea.slice(inicioConcepto, idxMonto > -1 ? idxMonto : undefined),
      ];

      // Avanzar por las líneas de continuación hasta el siguiente movimiento;
      // de paso capturar el día suelto y enriquecer la descripción.
      let j = i + 1;
      while (j < seccion.length && !esMovimiento(seccion[j])) {
        const cont = seccion[j];
        const mSolo = cont.match(RE_SOLO_DIA);
        if (mSolo && dia == null) {
          dia = parseInt(mSolo[1], 10);
        } else if (!mSolo) {
          partesConcepto.push(cont);
        }
        j++;
      }

      let fecha = null;
      if (dia != null && periodo) {
        fecha = parsearFecha(`${dia}/${periodo.mes + 1}/${periodo.anio}`, anioRef, ["D/M/YYYY"]);
      }

      let tipo = null;
      if (prevSaldo != null) {
        tipo = round2(saldo - prevSaldo) >= 0 ? "cargo" : "abono";
      }
      prevSaldo = saldo;

      movimientos.push({
        fecha,
        monto: round2(monto),
        tipo,
        concepto: limpiarConcepto(partesConcepto.join(" ")),
        saldo,
        raw: linea,
      });

      i = j;
    }

    return movimientos;
  },
};

export default banregio;
